import { useEffect, useMemo, useState } from 'react';
import { Copy, TriangleAlert } from 'lucide-react';
import {
  apiErrorText,
  apiKeysApi,
  type GrantableScope,
  type IssuedApiKey,
} from '@/shared/api';
import {
  Button,
  Callout,
  Checkbox,
  Combobox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Field,
  Input,
  Select,
  Textarea,
  toast,
} from '@/shared/ui';
import styles from './ApiKeyDialog.module.css';

const TTL_OPTIONS = [
  { value: '30', label: '30 дней' },
  { value: '90', label: '90 дней' },
  { value: '180', label: 'Полгода' },
  { value: '365', label: 'Год' },
];

interface ApiKeyDialogProps {
  open: boolean;
  mode: 'personal' | 'service';
  grantable: GrantableScope[];
  roles?: { key: string; name: string }[];
  onClose: () => void;
  onIssued: () => void;
}

function parseIps(raw: string): string[] {
  return raw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ApiKeyDialog({ open, mode, grantable, roles = [], onClose, onIssued }: ApiKeyDialogProps) {
  const [name, setName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [role, setRole] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);
  const [canWrite, setCanWrite] = useState(false);
  const [ttl, setTtl] = useState('90');
  const [ipsRaw, setIpsRaw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [issued, setIssued] = useState<IssuedApiKey | null>(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setAccountName('');
    setRole('');
    setScopes([]);
    setCanWrite(false);
    setTtl('90');
    setIpsRaw('');
    setIssued(null);
  }, [open]);

  const scopeOptions = useMemo(
    () => grantable.map((g) => ({ value: g.key, label: g.label, group: g.group, keywords: g.key })),
    [grantable],
  );
  const roleOptions = useMemo(() => roles.map((r) => ({ value: r.key, label: r.name })), [roles]);

  const isService = mode === 'service';
  const dirty = !issued && (name !== '' || accountName !== '' || scopes.length > 0 || ipsRaw !== '');
  const missing =
    !name.trim()
      ? 'название ключа'
      : isService && !accountName.trim()
        ? 'название интеграции'
        : isService && !role
          ? 'роль служебной учётки'
          : scopes.length === 0
            ? 'хотя бы одно право'
            : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (missing || submitting) return;
    setSubmitting(true);
    const base = {
      name: name.trim(),
      scopes,
      canWrite,
      expiresInDays: Number(ttl),
      allowedIps: parseIps(ipsRaw),
    };
    try {
      const res = isService
        ? await apiKeysApi.createService({ ...base, accountName: accountName.trim(), role })
        : await apiKeysApi.createMine(base);
      setIssued(res.data);
      onIssued();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось выпустить ключ'));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = () => {
    if (!issued) return;
    void navigator.clipboard.writeText(issued.token).then(
      () => toast.success('Ключ скопирован'),
      () => toast.error('Не удалось скопировать — выделите ключ и скопируйте вручную'),
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && !issued && onClose()}
      dirty={dirty}
      unsavedWhat="Заполненные поля"
    >
      <DialogContent>
        {issued ? (
          <>
            <DialogTitle>Ключ «{issued.key.name}» выпущен</DialogTitle>
            <Callout tone="warning" Icon={TriangleAlert} title="Скопируйте ключ сейчас">
              После закрытия окна его не покажем: в базе хранится только отпечаток. Потеряли — отзовите и выпустите новый.
            </Callout>
            <pre className={styles.token}>{issued.token}</pre>
            <p className={styles.usage}>
              Передавайте в заголовке <code>Authorization: Bearer …</code>
            </p>
            <DialogFooter>
              <Button variant="ghost" type="button" onClick={copy}>
                <Copy size={14} aria-hidden />
                Скопировать
              </Button>
              <Button type="button" onClick={onClose}>
                Готово
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogTitle>{isService ? 'Ключ интеграции' : 'Новый API-ключ'}</DialogTitle>
            <DialogDescription>
              {isService
                ? 'Интеграция получит свою служебную учётку: заявки, созданные ключом, будут от её имени.'
                : 'Ключ действует от вашего имени и никогда не шире ваших прав: снимут роль — сузится и ключ.'}
            </DialogDescription>
            <form onSubmit={submit}>
              {isService && (
                <Field label="Интеграция" required hint="Так служебная учётка будет подписана в заявках и журнале.">
                  <Input
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder="1С: учёт техники"
                    maxLength={100}
                    autoFocus
                  />
                </Field>
              )}
              <Field label="Название ключа" required hint="Чтобы узнать ключ в списке: где и зачем он используется.">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Выгрузка заявок в отчёт"
                  maxLength={100}
                  autoFocus={!isService}
                />
              </Field>
              {isService && (
                <Field
                  label="Роль служебной учётки"
                  required
                  hint="Права ключа не выйдут за права этой роли. Доступны роли, все права которых есть у вас."
                >
                  <Select
                    value={role}
                    onValueChange={(v) => setRole(String(v ?? ''))}
                    options={roleOptions}
                    placeholder="Выберите роль"
                    aria-label="Роль служебной учётки"
                  />
                </Field>
              )}
              <Field label="Права ключа" required hint="Ровно то, что нужно интеграции. Можно выдать только свои права.">
                <Combobox
                  multiple
                  value={scopes}
                  onValueChange={setScopes}
                  options={scopeOptions}
                  placeholder="Найти право"
                  aria-label="Права ключа"
                />
              </Field>
              <Field label="Срок действия">
                <Select
                  value={ttl}
                  onValueChange={(v) => setTtl(String(v ?? '90'))}
                  options={TTL_OPTIONS}
                  aria-label="Срок действия"
                />
              </Field>
              <Field
                label="Адреса, с которых можно ходить"
                hint="IP или подсеть (10.0.0.0/24), по одному в строке. Пусто — с любого адреса."
              >
                <Textarea value={ipsRaw} onChange={(e) => setIpsRaw(e.target.value)} placeholder="10.20.0.0/16" rows={2} />
              </Field>
              <Checkbox
                checked={canWrite}
                onCheckedChange={(v) => setCanWrite(v === true)}
                label="Разрешить изменения"
                hint="Без этого ключ только читает: создать, изменить или удалить по нему ничего нельзя."
              />
              <DialogFooter>
                <DialogClose render={<Button variant="ghost" type="button">Отмена</Button>} />
                <Button type="submit" aria-disabled={missing != null || submitting} title={missing ? `Не хватает: ${missing}` : undefined}>
                  {submitting ? 'Выпускаю…' : 'Выпустить ключ'}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
